import firebase from 'firebase/compat/app'
const fb = require('../firebaseConfig.js')

function normalizeHeader (header) {
  if (!header || !header.machine_name || !header.label) return null
  return {
    machineName: header.machine_name,
    label: header.label
  }
}

function normalizeTimestamp (timestamp) {
  if (timestamp && typeof timestamp.toDate === 'function') return timestamp
  if (timestamp instanceof Date) return firebase.firestore.Timestamp.fromDate(timestamp)
  return firebase.firestore.Timestamp.now()
}

/** Save current datapoint doc to revisions subcollection before making changes (for log history updates). */
async function saveDatapointRevisionBeforeUpdate (dataPointRef) {
  const doc = await dataPointRef.get()
  if (!doc.exists) return
  const revisionData = { ...doc.data() }
  delete revisionData.id
  await dataPointRef.collection('revisions').add(revisionData)
}

/** Save current block doc to revisions subcollection before making changes (for log history updates). */
async function saveBlockRevisionBeforeUpdate (blockRef) {
  const doc = await blockRef.get()
  if (!doc.exists) return
  const revisionData = { ...doc.data() }
  delete revisionData.id
  await blockRef.collection('revisions').add(revisionData)
}

async function appendDatapointLogEntry ({
  collectorId,
  dataPointId,
  farmId,
  header,
  value,
  notes,
  userId,
  timestamp
}) {
  const h = normalizeHeader(header)
  if (!collectorId || !dataPointId || !h) throw new Error('Missing required datapoint log parameters')
  const entryTs = normalizeTimestamp(timestamp)
  const createdOn = firebase.firestore.Timestamp.now()
  const dataPointRef = fb.collectorsCollection.doc(collectorId).collection('dataPoints').doc(dataPointId)
  const logEntry = {
    headerMachineName: h.machineName,
    headerLabel: h.label,
    value: value == null ? null : value,
    timestamp: entryTs,
    notes: notes || '',
    userId: userId || null,
    createdOn,
    farmId: farmId || null,
    collectorId,
    dataPointId
  }
  const logRef = await dataPointRef.collection('logs').add(logEntry)
  await saveDatapointRevisionBeforeUpdate(dataPointRef)
  // Recompute from logs so displayed value is the entry with latest log date, not just the one we added
  await recomputeDatapointLatest(dataPointRef, h.machineName, `Log entry added for ${h.label}`)
  return { id: logRef.id, ...logEntry }
}

async function appendBlockLogEntry ({
  blockId,
  farmId,
  header,
  value,
  notes,
  userId,
  timestamp
}) {
  const h = normalizeHeader(header)
  if (!blockId || !h) throw new Error('Missing required block log parameters')
  const entryTs = normalizeTimestamp(timestamp)
  const createdOn = firebase.firestore.Timestamp.now()
  const blockRef = fb.blocksCollection.doc(blockId)
  const logEntry = {
    headerMachineName: h.machineName,
    headerLabel: h.label,
    value: value == null ? null : value,
    timestamp: entryTs,
    notes: notes || '',
    userId: userId || null,
    createdOn,
    farmId: farmId || null,
    blockId
  }
  const logRef = await blockRef.collection('logs').add(logEntry)
  await saveBlockRevisionBeforeUpdate(blockRef)
  // Recompute from logs so displayed value is the entry with latest log date, not just the one we added
  await recomputeBlockLatest(blockRef, h.machineName, `Log entry added for ${h.label}`)
  return { id: logRef.id, ...logEntry }
}

function getLatestLogFromParent (docData, headerMachineName) {
  if (!docData || !docData.latestLogs) return null
  return docData.latestLogs[headerMachineName] || null
}

async function getDatapointLogHistory ({ collectorId, dataPointId, headerMachineName, limit = 10, startAfterDoc = null }) {
  if (!collectorId || !dataPointId || !headerMachineName) return { entries: [], lastDoc: null }
  let query = fb.collectorsCollection
    .doc(collectorId)
    .collection('dataPoints')
    .doc(dataPointId)
    .collection('logs')
    .where('headerMachineName', '==', headerMachineName)
    .orderBy('timestamp', 'desc')
    .limit(limit)
  if (startAfterDoc) query = query.startAfter(startAfterDoc)
  const snap = await query.get()
  const entries = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
  const lastDoc = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null
  return { entries, lastDoc }
}

/**
 * Fetch every log entry for a collector across all of its datapoints and
 * headers — for bulk export. Walks each datapoint's `logs` subcollection
 * directly (rather than a collectionGroup query) so it relies only on the
 * existing per-path security rules and needs no collection-group index.
 * Datapoints are read in parallel chunks. Sorted newest-first.
 */
async function getAllCollectorLogs ({ collectorId, chunkSize = 20 }) {
  if (!collectorId) return []
  const dataPointsRef = fb.collectorsCollection.doc(collectorId).collection('dataPoints')
  const dpSnap = await dataPointsRef.get()
  const dpIds = dpSnap.docs.map(d => d.id)

  const entries = []
  for (let i = 0; i < dpIds.length; i += chunkSize) {
    const chunk = dpIds.slice(i, i + chunkSize)
    const chunkResults = await Promise.all(chunk.map(dpId =>
      dataPointsRef.doc(dpId).collection('logs').get()
        .then(snap => snap.docs.map(doc => {
          const data = doc.data()
          return { id: doc.id, ...data, dataPointId: data.dataPointId || dpId }
        }))
        .catch(() => [])
    ))
    chunkResults.forEach(rows => { for (const row of rows) entries.push(row) })
  }

  entries.sort((a, b) => getTimestampMillis(b.timestamp) - getTimestampMillis(a.timestamp))
  return entries
}

async function getBlockLogHistory ({ blockId, headerMachineName, limit = 10, startAfterDoc = null }) {
  if (!blockId || !headerMachineName) return { entries: [], lastDoc: null }
  let query = fb.blocksCollection
    .doc(blockId)
    .collection('logs')
    .where('headerMachineName', '==', headerMachineName)
    .orderBy('timestamp', 'desc')
    .limit(limit)
  if (startAfterDoc) query = query.startAfter(startAfterDoc)
  const snap = await query.get()
  const entries = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
  const lastDoc = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null
  return { entries, lastDoc }
}

function getTimestampMillis (value) {
  if (!value) return 0
  if (typeof value.toDate === 'function') return value.toDate().getTime()
  if (value instanceof Date) return value.getTime()
  return 0
}

function selectLatestLogDoc (docs) {
  if (!docs || !docs.length) return null

  const newestTimestamp = getTimestampMillis(docs[0].data().timestamp)
  let latestDoc = docs[0]
  let latestCreatedOn = getTimestampMillis(docs[0].data().createdOn)

  docs.forEach(doc => {
    const data = doc.data()
    if (getTimestampMillis(data.timestamp) !== newestTimestamp) return

    const createdOnMs = getTimestampMillis(data.createdOn)
    if (createdOnMs > latestCreatedOn) {
      latestDoc = doc
      latestCreatedOn = createdOnMs
    }
  })

  return latestDoc
}

async function recomputeDatapointLatest (dataPointRef, headerMachineName, revisionMessage = null) {
  const snap = await dataPointRef.collection('logs')
    .where('headerMachineName', '==', headerMachineName)
    .orderBy('timestamp', 'desc')
    .limit(10)
    .get()
  const updates = { updatedOn: new Date() }
  if (revisionMessage) updates.revisionMessage = revisionMessage
  if (snap.empty) {
    updates[`latestLogs.${headerMachineName}`] = firebase.firestore.FieldValue.delete()
    updates[headerMachineName] = firebase.firestore.FieldValue.delete()
  } else {
    const doc = selectLatestLogDoc(snap.docs)
    const d = doc.data()
    updates[`latestLogs.${headerMachineName}`] = {
      value: d.value,
      timestamp: d.timestamp,
      notes: d.notes || '',
      userId: d.userId || null
    }
    updates[headerMachineName] = d.value
  }
  await dataPointRef.update(updates)
}

async function recomputeBlockLatest (blockRef, headerMachineName, revisionMessage = null) {
  const snap = await blockRef.collection('logs')
    .where('headerMachineName', '==', headerMachineName)
    .orderBy('timestamp', 'desc')
    .limit(10)
    .get()
  const updates = { updatedOn: new Date() }
  if (revisionMessage) updates.revisionMessage = revisionMessage
  if (snap.empty) {
    updates[`latestLogs.${headerMachineName}`] = firebase.firestore.FieldValue.delete()
    updates[headerMachineName] = firebase.firestore.FieldValue.delete()
  } else {
    const doc = selectLatestLogDoc(snap.docs)
    const d = doc.data()
    updates[`latestLogs.${headerMachineName}`] = {
      value: d.value,
      timestamp: d.timestamp,
      notes: d.notes || '',
      userId: d.userId || null
    }
    updates[headerMachineName] = d.value
  }
  await blockRef.update(updates)
}

async function updateDatapointLogEntry ({ collectorId, dataPointId, header, entryId, value, notes, timestamp }) {
  const h = normalizeHeader(header)
  if (!collectorId || !dataPointId || !h || !entryId) throw new Error('Missing required params for update datapoint log')
  const dataPointRef = fb.collectorsCollection.doc(collectorId).collection('dataPoints').doc(dataPointId)
  const entryRef = dataPointRef.collection('logs').doc(entryId)
  const updates = {}
  if (value !== undefined) updates.value = value
  if (notes !== undefined) updates.notes = notes || ''
  if (timestamp !== undefined) updates.timestamp = normalizeTimestamp(timestamp)
  if (Object.keys(updates).length === 0) return
  await saveDatapointRevisionBeforeUpdate(dataPointRef)
  await entryRef.update(updates)
  await recomputeDatapointLatest(dataPointRef, h.machineName, `Log entry updated for ${h.label}`)
}

async function updateBlockLogEntry ({ blockId, header, entryId, value, notes, timestamp }) {
  const h = normalizeHeader(header)
  if (!blockId || !h || !entryId) throw new Error('Missing required params for update block log')
  const blockRef = fb.blocksCollection.doc(blockId)
  const entryRef = blockRef.collection('logs').doc(entryId)
  const updates = {}
  if (value !== undefined) updates.value = value
  if (notes !== undefined) updates.notes = notes || ''
  if (timestamp !== undefined) updates.timestamp = normalizeTimestamp(timestamp)
  if (Object.keys(updates).length === 0) return
  await saveBlockRevisionBeforeUpdate(blockRef)
  await entryRef.update(updates)
  await recomputeBlockLatest(blockRef, h.machineName, `Log entry updated for ${h.label}`)
}

async function deleteDatapointLogEntry ({ collectorId, dataPointId, header, entryId }) {
  const h = normalizeHeader(header)
  if (!collectorId || !dataPointId || !h || !entryId) throw new Error('Missing required params for delete datapoint log')
  const dataPointRef = fb.collectorsCollection.doc(collectorId).collection('dataPoints').doc(dataPointId)
  await saveDatapointRevisionBeforeUpdate(dataPointRef)
  await dataPointRef.collection('logs').doc(entryId).delete()
  await recomputeDatapointLatest(dataPointRef, h.machineName, `Log entry deleted for ${h.label}`)
}

async function deleteBlockLogEntry ({ blockId, header, entryId }) {
  const h = normalizeHeader(header)
  if (!blockId || !h || !entryId) throw new Error('Missing required params for delete block log')
  const blockRef = fb.blocksCollection.doc(blockId)
  await saveBlockRevisionBeforeUpdate(blockRef)
  await blockRef.collection('logs').doc(entryId).delete()
  await recomputeBlockLatest(blockRef, h.machineName, `Log entry deleted for ${h.label}`)
}

async function migrateStaticDatapointValueToLog ({
  collectorId,
  dataPointId,
  farmId,
  header,
  value,
  userId
}) {
  if (value == null || value === '') return null
  return appendDatapointLogEntry({
    collectorId,
    dataPointId,
    farmId,
    header,
    value,
    notes: 'Migrated from static header value',
    userId,
    timestamp: new Date()
  })
}

async function migrateStaticBlockValueToLog ({
  blockId,
  farmId,
  header,
  value,
  userId
}) {
  if (value == null || value === '') return null
  return appendBlockLogEntry({
    blockId,
    farmId,
    header,
    value,
    notes: 'Migrated from static header value',
    userId,
    timestamp: new Date()
  })
}

export {
  appendDatapointLogEntry,
  appendBlockLogEntry,
  getLatestLogFromParent,
  getDatapointLogHistory,
  getBlockLogHistory,
  getAllCollectorLogs,
  updateDatapointLogEntry,
  updateBlockLogEntry,
  deleteDatapointLogEntry,
  deleteBlockLogEntry,
  migrateStaticDatapointValueToLog,
  migrateStaticBlockValueToLog
}
